
import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import ServicesHero from './components/ServicesHero';
import ServicesOverview from './components/ServicesOverview';
import ServiceDetail from './components/ServiceDetail';
import ServiceProcess from './components/ServiceProcess';
import ServicesCTA from './components/ServicesCTA';
import MobileCallButton from '../../components/feature/MobileCallButton';

const roofingFeatures = [
  { icon: 'ri-shield-check-line', title: 'Asphalt Shingles', description: 'GAF & Owens Corning certified systems with industry-leading warranties.' },
  { icon: 'ri-building-2-line', title: 'Metal Roofing', description: 'Standing seam and corrugated metal options for maximum durability.' },
  { icon: 'ri-tools-line', title: 'Roof Repairs', description: 'Leak detection, flashing repair, and emergency patching services.' },
  { icon: 'ri-search-eye-line', title: 'Free Inspections', description: 'Comprehensive roof assessments with detailed photo reports.' },
];

const sidingFeatures = [
  { icon: 'ri-layout-column-line', title: 'Vinyl Siding', description: 'Low-maintenance, fade-resistant vinyl in dozens of colors and styles.' },
  { icon: 'ri-stack-line', title: 'Fiber Cement', description: 'James Hardie fiber cement boards for premium durability and aesthetics.' },
  { icon: 'ri-paint-brush-line', title: 'Custom Colors', description: 'Wide selection of colors and textures to match your vision.' },
  { icon: 'ri-temp-cold-line', title: 'Insulated Options', description: 'Energy-efficient insulated siding that lowers heating and cooling costs.' },
];

const gutterFeatures = [
  { icon: 'ri-drop-line', title: 'Seamless Gutters', description: 'Custom-fabricated on-site for a perfect fit with no leaky seams.' },
  { icon: 'ri-leaf-line', title: 'Leaf Guards', description: 'Gutter protection systems that eliminate clogs and reduce maintenance.' },
  { icon: 'ri-arrow-down-circle-line', title: 'Downspout Systems', description: 'Properly sized and positioned downspouts for optimal water flow.' },
  { icon: 'ri-palette-line', title: 'Color Matching', description: 'Gutters matched to your trim and exterior for a seamless look.' },
];

const stormFeatures = [
  { icon: 'ri-alarm-warning-line', title: 'Emergency Response', description: 'Rapid tarping and temporary repairs to prevent further damage.' },
  { icon: 'ri-file-shield-2-line', title: 'Insurance Claims', description: 'We work directly with your insurer to maximize your claim.' },
  { icon: 'ri-camera-line', title: 'Damage Documentation', description: 'Thorough photo and video documentation for your records.' },
  { icon: 'ri-refresh-line', title: 'Full Restoration', description: 'Complete roof, siding, and gutter restoration to pre-storm condition.' },
];

export default function ServicesPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative">
      <div className="fixed inset-0 z-0 flex items-center justify-center pointer-events-none">
        <img
          src="https://static.readdy.ai/image/69b3c4875e1562fa68a140ce2d237389/644ca808ee069ab97d6a9bc1f4617fe7.png"
          alt=""
          className="w-[900px] h-auto opacity-[0.05]"
        />
      </div>

      <Navigation isScrolled={isScrolled} />
      <MobileCallButton />
      <ServicesHero />
      <ServicesOverview />

      <ServiceDetail
        id="roofing-section"
        label="Most Popular"
        labelColor="#39FF14"
        title="Complete Roofing"
        titleAccent="Systems"
        description="Your roof is your home's first line of defense. IronCore delivers expert roof replacements, repairs, and inspections using top-tier materials from GAF and Owens Corning. Whether you need a full tear-off and replacement or a targeted repair, our certified crews ensure every shingle is placed with precision. We stand behind our work with manufacturer-backed warranties and a commitment to quality that lasts decades."
        features={roofingFeatures}
        image="https://public.readdy.ai/ai/img_res/542e07b171bcbf5c452c9d60562b9583.jpg"
        imageAlt="Professional roofing installation by IronCore"
      />

      <div className="bg-gray-50">
        <ServiceDetail
          id="siding-section"
          label="Curb Appeal"
          labelColor="#39FF14"
          title="Premium Siding"
          titleAccent="Installation"
          description="Transform your home's exterior with beautiful, durable siding from IronCore. We specialize in vinyl, fiber cement, and engineered wood siding that not only looks stunning but also provides superior insulation and weather protection. Our expert installers ensure every panel is perfectly aligned and sealed, giving your home a fresh, modern appearance that boosts value and reduces energy costs for years to come."
          features={sidingFeatures}
          image="https://readdy.ai/api/search-image?query=Beautiful%20modern%20suburban%20home%20exterior%20with%20freshly%20installed%20light%20gray%20fiber%20cement%20siding%2C%20clean%20horizontal%20lines%2C%20white%20trim%20details%2C%20professional%20architectural%20photography%2C%20manicured%20front%20yard%20with%20green%20lawn%2C%20warm%20golden%20hour%20lighting%2C%20showing%20premium%20quality%20siding%20craftsmanship&width=800&height=600&seq=services-siding-detail-001&orientation=landscape"
          imageAlt="Premium siding installation by IronCore"
          reversed
        />
      </div>

      <ServiceDetail
        id="gutters-section"
        label="Year-Round Protection"
        labelColor="#39FF14"
        title="Seamless Gutter"
        titleAccent="Solutions"
        description="Protect your foundation, landscaping, and siding with IronCore's seamless gutter systems. Our gutters are custom-fabricated on-site to fit your home perfectly — no seams means no leaks. We also install high-performance leaf guard systems that keep debris out and water flowing freely. With a variety of colors and materials available, your new gutters will look great and perform flawlessly through every season."
        features={gutterFeatures}
        image="https://readdy.ai/api/search-image?query=Close%20up%20of%20new%20white%20seamless%20aluminum%20gutter%20system%20installed%20on%20residential%20home%20showing%20clean%20downspout%20connection%20and%20leaf%20guard%20protection%2C%20professional%20installation%20detail%20%2C%20warm%20natural%20lighting%20%2C%20house%20exterior%20with%20dark%20shingle%20roof%20visible%20above%2C%20quality%20craftsmanship%20photography&width=800&height=600&seq=services-gutters-detail-001&orientation=landscape"
        imageAlt="Seamless gutter installation by IronCore"
      />

      <div className="bg-gray-50">
        <ServiceDetail
          id="storm-section"
          label="Emergency Service"
          labelColor="#FF4444"
          title="Storm Damage"
          titleAccent="Restoration"
          description="When severe weather strikes, IronCore is the team you want on your side. We provide rapid emergency response to secure your property, followed by a comprehensive damage assessment and full restoration. Our storm specialists work directly with your insurance company to handle the entire claims process, ensuring you get the coverage you deserve. From emergency tarping to complete roof and exterior rebuilds, we restore your home to its pre-storm condition — fast."
          features={stormFeatures}
          image="https://readdy.ai/api/search-image?query=Professional%20roofing%20contractor%20inspecting%20storm%20damaged%20residential%20roof%2C%20worker%20in%20safety%20gear%20examining%20hail%20damage%20on%20asphalt%20shingles%2C%20dramatic%20overcast%20sky%20in%20background%20%2C%20insurance%20claim%20documentation%20process%20%2C%20construction%20safety%20equipment%20visible%20%2C%20realistic%20storm%20restoration%20work%20photography&width=800&height=600&seq=services-storm-detail-001&orientation=landscape"
          imageAlt="Storm damage restoration by IronCore"
          reversed
        />
      </div>

      <ServiceProcess />
      <ServicesCTA />
      <Footer />
    </div>
  );
}
